import React, { useState, useRef, useCallback } from "react";
import { metricsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import {
  Sparkles,
  CheckCircle,
  XCircle,
  X,
  Pencil,
  Trash2,
} from "lucide-react";

const TitleCaseText = ({ text }) => {
  const toTitleCase = (str) =>
    str
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  return <span>{toTitleCase(text)}</span>;
};

const GenerateMetricsModal = ({ isOpen, onClose, onMetricsCreated }) => {
  const { currentWorkspace } = useAuth();
  const { selectedAgent: agentFromContext } = useAgent();
  const [loading, setLoading] = useState(false);
  const [generatedMetrics, setGeneratedMetrics] = useState([]);
  const [error, setError] = useState(null);
  const [numberOfMetrics, setNumberOfMetrics] = useState(1);
  const [focusArea, setFocusArea] = useState("");
  const [deletingMetric, setDeletingMetric] = useState(null);
  const [progress, setProgress] = useState("");
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollForResult = useCallback((generationId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await metricsAPI.getGenerateStatus(currentWorkspace.id, generationId);
        const data = res.data;

        if (data.status === "completed") {
          stopPolling();
          setGeneratedMetrics(data.metrics || []);
          setLoading(false);
          setProgress("");
        } else if (data.status === "failed") {
          stopPolling();
          setError(data.error || "Generation failed");
          setLoading(false);
          setProgress("");
        } else {
          setProgress(data.progress || "Processing...");
        }
      } catch {
        stopPolling();
        setError("Failed to check generation status");
        setLoading(false);
        setProgress("");
      }
    }, 2000);
  }, [currentWorkspace.id, stopPolling]);

  const handleGenerate = async () => {
    if (!agentFromContext?.id) {
      setError("No agent selected. Please select an agent from the sidebar first.");
      return;
    }
    setLoading(true);
    setError(null);
    setProgress("Starting generation...");
    try {
      const response = await metricsAPI.generateMetrics(
        agentFromContext.id,
        currentWorkspace.id,
        { numberOfMetrics, focusArea }
      );
      const { generation_id } = response.data;
      pollForResult(generation_id);
    } catch (err) {
      setError("Failed to generate metrics");
      setLoading(false);
      setProgress("");
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await metricsAPI.createGeneratedMetrics(
        generatedMetrics,
        currentWorkspace.id,
        agentFromContext?.id
      );
      setGeneratedMetrics([]);
      setFocusArea("");
      setNumberOfMetrics(1);
      onMetricsCreated?.();
      onClose();
    } catch (err) {
      setError("Failed to create metrics");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (index) => {
    setDeletingMetric(index);
  };

  const handleDeleteConfirm = () => {
    setGeneratedMetrics((prev) => prev.filter((_, i) => i !== deletingMetric));
    setDeletingMetric(null);
  };

  const handleClose = () => {
    stopPolling();
    setGeneratedMetrics([]);
    setError(null);
    setProgress("");
    setFocusArea("");
    setNumberOfMetrics(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-10">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Generate Metrics by AI</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {agentFromContext?.name ? `For ${agentFromContext.name}` : "Select an agent first"}
            </p>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-muted-foreground transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {generatedMetrics.length === 0 ? (
            /* ── Generation Form ── */
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Agent</label>
                  <input
                    type="text"
                    value={agentFromContext?.name || "No agent selected"}
                    disabled
                    className="block w-full rounded-lg border-border bg-muted/50 text-muted-foreground text-sm cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Number of metrics</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={numberOfMetrics}
                    onChange={(e) => setNumberOfMetrics(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                    className="block w-full rounded-lg border-input shadow-sm focus:border-black focus:ring-black text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Focus area / additional instructions
                  <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                </label>
                <textarea
                  value={focusArea}
                  onChange={(e) => setFocusArea(e.target.value)}
                  rows={3}
                  className="block w-full rounded-lg border-input shadow-sm focus:border-black focus:ring-black text-sm"
                  placeholder="e.g. Focus on compliance and regulatory metrics, or generate metrics that evaluate customer satisfaction and empathy..."
                />
              </div>
              {loading && progress && (
                <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                  <svg className="animate-spin h-5 w-5 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm text-muted-foreground">{progress}</span>
                </div>
              )}
              {error && (
                <div className="flex items-center text-sm text-red-600">
                  <XCircle className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  {error}
                </div>
              )}
            </div>
          ) : (
            /* ── Generated Results ── */
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-muted-foreground mb-2">{generatedMetrics.length} metrics generated</p>
              {generatedMetrics.map((metric, index) => (
                <div key={index} className="border border-border rounded-lg p-4 hover:border-input transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-foreground truncate">{metric.name}</h4>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                          <TitleCaseText text={metric.metrics_type} />
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 capitalize">
                          {metric.result_type || "boolean"}
                        </span>
                      </div>
                      {metric.llm_instructions && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{metric.llm_instructions}</p>
                      )}
                      {metric.evaluation_trigger?.type && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Trigger: {metric.evaluation_trigger.type === "always" ? "Always" : "On call state"}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(index)}
                      className="p-1.5 ml-2 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-border">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-lg hover:bg-muted/50 transition-colors"
          >
            Cancel
          </button>
          {generatedMetrics.length === 0 ? (
            <button
              onClick={handleGenerate}
              disabled={loading || !agentFromContext?.id}
              className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate
                </>
              )}
            </button>
          ) : (
            <>
              <button
                onClick={() => setGeneratedMetrics([])}
                className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-lg hover:bg-muted/50 transition-colors"
              >
                Regenerate
              </button>
              <button
                onClick={handleApprove}
                disabled={loading}
                className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve All ({generatedMetrics.length})
              </button>
            </>
          )}
        </div>

        {/* Delete Confirmation */}
        {deletingMetric !== null && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
            <div className="bg-card rounded-xl shadow-xl max-w-sm w-full p-4 mx-4">
              <h3 className="text-lg font-semibold text-foreground mb-2">Remove metric?</h3>
              <p className="text-sm text-muted-foreground mb-5">This will remove it from the generated list.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeletingMetric(null)} className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-lg hover:bg-muted/50">
                  Cancel
                </button>
                <button onClick={handleDeleteConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateMetricsModal;
